import { useState, useRef, useCallback, useEffect } from "react";

import {
  Text,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Stack,
  StackProps,
  Tooltip,
  useMantineTheme,
  TextProps,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import _ from "lodash";
import router from "next/router";

import { Geographies } from "constants/regions";
import { dictionaryToSearchParams } from "utils/utils";

import { useIsLoadingSearchData, useSearchRouteInfo } from "../hooks/routing";

const TIER_OPTIONS = [
  {
    label: "Tier 1",
    value: "1",
    hover: "San Francisco Bay Area, New York City, Seattle, Los Angeles",
  },
  {
    label: "Tier 2",
    value: "2",
    hover:
      "Austin, Boston, Chicago, Denver, Portland, Miami, Sacramento, San Diego, Washington D.C. ",
  },
  {
    label: "Tier 3",
    value: "3",
    hover:
      "Other U.S. Metros (ex. Atlanta, Columbus, Dallas, Durham, Houston, Phoenix, Raleigh, Las Vegas, Charlotte, Salt Lake City)",
  },
];

const LOC = [
  { label: "California", value: Geographies.CA, indent: false },
  { label: "San Francisco Bay Area", value: Geographies.SF, indent: true },
  { label: "New York", value: Geographies.NY, indent: false },
  { label: "New York City", value: Geographies.NYC, indent: true },
  { label: "Texas", value: Geographies.TX, indent: false },
  { label: "Washington", value: Geographies.WA, indent: false },
  { label: "All U.S.", value: Geographies.US, indent: false },
  { label: "Canada", value: Geographies.CANADA, indent: false },
  { label: "International", value: Geographies.INTL, indent: false },
  { label: "Remote", value: Geographies.REMOTE, indent: false },
];

export function LocationSelector(props: Omit<StackProps, "children">): JSX.Element {
  const { baseRoute, query, replaceQuery, queryString, focusedJobTitle, searchMode } =
    useSearchRouteInfo();
  const [selectedTiers, setSelectedTiers] = useState(query.tier);
  const [selectedLocations, setSelectedLocations] = useState(query.location);

  const nextRouteData = useRef(_.clone(query));

  const loading = useIsLoadingSearchData();
  const theme = useMantineTheme();

  function handleDeselectAll() {
    setSelectedTiers([]);
    setSelectedLocations([]);
    replaceQuery({
      tier: [],
      location: [],
    });
  }

  useEffect(() => {
    nextRouteData.current = _.clone(query);
  }, [queryString, query]);

  const handleChange = useCallback((name: string, v: string[] | [number, number] | null) => {
    const nextQuery = nextRouteData.current;
    if (v) {
      _.set(nextQuery, name, v);
    } else {
      _.set(nextQuery, name, []);
    }

    _.set(nextQuery, "offset", 0);

    const nextQueryString = dictionaryToSearchParams(nextQuery);

    router.push(`${baseRoute}/${nextQueryString}`, undefined, { scroll: false });
  }, []);

  function renderTierOptions() {
    return (
      <Checkbox.Group
        value={selectedTiers}
        onChange={(v) => {
          handleChange("tier", v);
          setSelectedTiers(v);
        }}
        size="xs"
        withAsterisk
      >
        <Stack w="100%">
          {_.map(TIER_OPTIONS, (option) => (
            <Checkbox
              key={option.value}
              value={option.value}
              label={
                <Group
                  align="center"
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    cursor: "pointer",
                    fontSize: "14px",
                    justifyContent: "center",
                  }}
                >
                  <Text
                    sx={{
                      paddingLeft: 24,
                    }}
                  >
                    {option.label}
                  </Text>
                  <Tooltip label={option.hover}>
                    <IconInfoCircle
                      size={16}
                      style={{
                        position: "absolute",
                        marginLeft: 8,
                        left: 65,
                        top: 5,
                      }}
                    />
                  </Tooltip>
                </Group>
              }
              styles={{
                root: {
                  display: "flex",
                  alignItems: "center",
                  padding: "4px 2px",
                  position: "relative",
                  cursor: "pointer",
                  borderRadius: "4px",
                  ":hover": {
                    backgroundColor: theme.colors.gray[3],
                  },
                },
              }}
            />
          ))}
        </Stack>
      </Checkbox.Group>
    );
  }

  function renderLocationOptions() {
    return (
      <Checkbox.Group
        value={query.location}
        // @ts-ignore
        onChange={(v: Geographies[]) => {
          // Don't auto-select related locations - let users choose exactly what they want
          handleChange("location", v);
          setSelectedLocations(v);
        }}
        size="xs"
        withAsterisk
      >
        <Stack w="100%">
          {_.map(LOC, (option) => (
            <Checkbox
              key={option.value}
              value={option.value}
              ml={option.indent ? 20 : 0}
              label={
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    cursor: "pointer",
                    fontSize: "14px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      paddingLeft: 24,
                    }}
                  >
                    {option.label}
                  </Box>
                </Box>
              }
              styles={{
                root: {
                  display: "flex",
                  alignItems: "center",
                  padding: "4px 2px",
                  position: "relative",
                  cursor: "pointer",
                  borderRadius: "4px",
                  ":hover": {
                    backgroundColor: theme.colors.gray[3],
                  },
                },
              }}
            />
          ))}
        </Stack>
      </Checkbox.Group>
    );
  }

  return (
    <Stack {...props}>
      <SubHeader>Tiers</SubHeader>
      {renderTierOptions()}
      <Divider />
      <SubHeader>Geographies</SubHeader>
      {renderLocationOptions()}
      <Box>
        <Button variant="secondary" h={30} onClick={handleDeselectAll}>
          Clear
        </Button>
      </Box>
    </Stack>
  );
}

function SubHeader(props: TextProps): JSX.Element {
  return (
    <Text
      fz={12}
      c="gray.6"
      sx={{
        textTransform: "uppercase",
        fontWeight: 500,
        letterSpacing: "0.08em",
        padding: "4px 0px",
      }}
      {...props}
    />
  );
}
